/*
 * Madson in-cart special (Symmetry port).
 *
 * Two custom elements:
 *   <cart-special-eligibility>  sits in the layout, captures the magic-link
 *                               token on whatever page the ad lands on.
 *   <cart-special-offer>        the card itself, in the drawer and cart page.
 *
 * The magic link gates VISIBILITY only. The Shopify Buy X Get Y automatic
 * discount is the only thing that makes the gift free, and it applies to any
 * qualifying cart regardless of what this file does.
 */
(function () {
  var STORAGE_KEY = 'madson-cart-special-eligibility';
  var ELIGIBILITY_DAYS = 14;

  function getActiveTokens() {
    var raw = '';
    try {
      raw = new URLSearchParams(window.location.search).get('aca') || '';
    } catch (error) {
      raw = '';
    }
    return raw
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);
  }

  function readRecord() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  /* Returns the stored record when the current URL carries the token, writing
   * it on first sight. capturedAt is what the countdown runs from, so it is
   * preserved across visits rather than reset on every page load. */
  function captureEligibility(token) {
    if (!token || getActiveTokens().indexOf(token) === -1) return null;

    var existing = readRecord();
    if (existing && existing.token === token && Number(existing.expiresAt) > Date.now()) {
      return existing;
    }

    var now = Date.now();
    var record = { token: token, capturedAt: now, expiresAt: now + ELIGIBILITY_DAYS * 86400000 };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      // Storage unavailable. The current pageview stays eligible in memory.
    }
    return record;
  }

  function storedEligibility(token) {
    var record = readRecord();
    if (!token || !record || record.token !== token) return null;
    if (!(Number(record.expiresAt) > Date.now())) return null;
    return record;
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function CartSpecialEligibility() {
    return Reflect.construct(HTMLElement, [], CartSpecialEligibility);
  }
  CartSpecialEligibility.prototype = Object.create(HTMLElement.prototype);
  CartSpecialEligibility.prototype.constructor = CartSpecialEligibility;
  Object.setPrototypeOf(CartSpecialEligibility, HTMLElement);
  CartSpecialEligibility.prototype.connectedCallback = function () {
    captureEligibility((this.dataset.magicToken || '').trim());
  };

  function CartSpecialOffer() {
    return Reflect.construct(HTMLElement, [], CartSpecialOffer);
  }
  CartSpecialOffer.prototype = Object.create(HTMLElement.prototype);
  CartSpecialOffer.prototype.constructor = CartSpecialOffer;
  Object.setPrototypeOf(CartSpecialOffer, HTMLElement);

  CartSpecialOffer.prototype.connectedCallback = function () {
    var token = (this.dataset.magicToken || '').trim();
    var magicOnly = this.dataset.magicOnly === 'true';
    var record = captureEligibility(token) || storedEligibility(token);

    if (magicOnly && !record) return;

    this.record = record;
    if (!this.startTimer(record)) return;

    this.hidden = false;
    this.bindAddToCart();
  };

  CartSpecialOffer.prototype.disconnectedCallback = function () {
    this.stopTimer();
  };

  /* Returns false when the offer window has already closed, so the caller
   * leaves the card hidden. */
  CartSpecialOffer.prototype.startTimer = function (record) {
    var minutes = parseInt(this.dataset.timerMinutes, 10);
    if (!minutes || minutes < 1) return true;

    // With no stored capture (magic gate off) the window opens on this view.
    var capturedAt = record && Number(record.capturedAt) ? Number(record.capturedAt) : Date.now();
    this.deadline = capturedAt + minutes * 60000;
    if (this.deadline <= Date.now()) return false;

    var output = this.querySelector('[data-cart-special-timer]');
    if (!output) return true;

    var self = this;
    var tick = function () {
      var remaining = self.deadline - Date.now();
      if (remaining <= 0) {
        self.stopTimer();
        self.hidden = true;
        return;
      }
      var totalSeconds = Math.floor(remaining / 1000);
      var hours = Math.floor(totalSeconds / 3600);
      var minutesLeft = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      output.textContent = hours > 0
        ? hours + ':' + pad(minutesLeft) + ':' + pad(seconds)
        : pad(minutesLeft) + ':' + pad(seconds);
    };

    tick();
    this.setAttribute('data-timer-active', 'true');
    this.timerId = setInterval(tick, 1000);
    return true;
  };

  CartSpecialOffer.prototype.stopTimer = function () {
    if (this.timerId) clearInterval(this.timerId);
    this.timerId = null;
  };

  CartSpecialOffer.prototype.bindAddToCart = function () {
    var button = this.querySelector('[data-cart-special-add]');
    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';

    var self = this;
    button.addEventListener('click', function () {
      self.addToCart(button);
    });
  };

  CartSpecialOffer.prototype.addToCart = function (button) {
    var select = this.querySelector('[data-cart-special-variant]');
    var variantId = select ? select.value : button.dataset.variantId;
    if (!variantId) return;

    var payload = { id: Number(variantId), quantity: 1 };
    var ruleName = (this.dataset.ruleName || '').trim();
    if (ruleName) payload.properties = { _Gifted: ruleName };

    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.addingLabel || 'Adding...';
    this.removeAttribute('data-has-error');

    var self = this;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add-to-cart failed with ' + response.status);
        // Same refresh signal the native drawer upsell uses.
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        // The re-rendered drawer drops this card, because the gift is now in
        // the cart. The cart page does not re-render, so reload it instead.
        if (self.dataset.surface !== 'drawer') window.location.reload();
      })
      .catch(function (error) {
        console.error('[cart-special-offer]', error);
        self.setAttribute('data-has-error', 'true');
        button.disabled = false;
        button.textContent = originalText;
      });
  };

  if (!customElements.get('cart-special-eligibility')) {
    customElements.define('cart-special-eligibility', CartSpecialEligibility);
  }
  if (!customElements.get('cart-special-offer')) {
    customElements.define('cart-special-offer', CartSpecialOffer);
  }
})();
